import type { ReactNode } from "react";

import type { IntlShape } from "react-intl";

import type {
  PriceLineItemProps,
  PriceLineItemTooltip,
} from "@/components/molecules/PriceLineItem";

import { rowTestIds } from "../../rowTestIds";
import type { PriceFacts, PriceRow, RowId } from "../types";
import { formatCurrency } from "./formatCurrency";
import { formatLabelRef } from "./formatLabelRef";
import { toPriceLineItemTooltip } from "./toPriceLineItemTooltip";

export type ToPriceRowPropsParams = {
  currency: string;
  /**
   * When true, whole row values render without decimals ("$700"). Labels,
   * captions and tooltips keep cents. Defaults to false.
   */
  excludeDecimals?: boolean;
  /**
   * Optional facts for detail explainers that need itemization
   * (e.g. Add-ons total modal).
   */
  facts?: PriceFacts | null;
  intl: IntlShape;
  /**
   * Adapter wraps the "View pricing rules" link inside the DIY space rental
   * tooltip. Receives the link label text and a `closeTooltip` callback to
   * dismiss the popover before opening the pricing-rules dialog. Omit to
   * render the tooltip's explainer text without a link.
   */
  renderPricingRulesLink?: (
    linkLabel: ReactNode,
    closeTooltip: () => void,
  ) => ReactNode;
  row: PriceRow;
  /**
   * When false, omit tooltip/detail explainers (e.g. price popovers).
   * Defaults to true.
   */
  showExplainers?: boolean;
  /**
   * Per-row tooltip override, keyed by `RowId`. When set for a row, it
   * replaces the catalog-driven tooltip entirely (bypasses `tooltipId`/
   * `detailId` resolution) — for call-site copy that doesn't belong in the
   * shared message catalog, e.g. a temporary experiment's explainer.
   */
  tooltipOverrides?: Partial<Record<RowId, PriceLineItemTooltip>>;
};

export type PriceRowLineItemProps = Pick<
  PriceLineItemProps,
  | "caption"
  | "captionValue"
  | "color"
  | "emphasis"
  | "label"
  | "leftOffset"
  | "middleContent"
  | "testId"
  | "tooltip"
  | "value"
>;

export { formatLabelRef } from "./formatLabelRef";

const formatPriceValue = ({
  currency,
  excludeDecimals,
  intl,
  negative,
  value,
}: {
  currency: string;
  excludeDecimals?: boolean;
  intl: IntlShape;
  negative?: boolean;
  value: PriceRow["value"];
}): string => {
  switch (value.kind) {
    case "money": {
      const amount = negative ? -Math.abs(value.amount) : value.amount;
      return formatCurrency({ amount, currency, excludeDecimals, intl });
    }
    case "label":
      return formatLabelRef({ currency, intl, ref: value.ref });
    case "percent":
      return `${value.value}%`;
    default:
      return "";
  }
};

/**
 * Maps a semantic `PriceRow` to DS `PriceLineItem` props.
 * Consumers pass `intl` — no `useIntl()` inside bindings.
 * `row.offset` maps 1:1 onto DS `leftOffset` (`none` | `left` | `indicator`).
 */
export const toPriceRowProps = ({
  currency,
  excludeDecimals = false,
  facts,
  intl,
  renderPricingRulesLink,
  row,
  showExplainers = true,
  tooltipOverrides,
}: ToPriceRowPropsParams): PriceRowLineItemProps => {
  return {
    caption: row.caption
      ? formatLabelRef({ currency, intl, ref: row.caption })
      : undefined,
    captionValue: row.valueCaption
      ? formatLabelRef({ currency, intl, ref: row.valueCaption })
      : undefined,
    color: row.color,
    emphasis: row.emphasis,
    label: formatLabelRef({ currency, intl, ref: row.label }),
    leftOffset: row.offset,
    middleContent: row.quantity != null ? `x${row.quantity}` : undefined,
    testId: rowTestIds[row.id],
    tooltip: showExplainers
      ? (tooltipOverrides?.[row.id] ??
        toPriceLineItemTooltip({
          currency,
          detailId: row.detail,
          facts,
          intl,
          renderPricingRulesLink,
          tooltipId: row.tooltipId,
        }))
      : undefined,
    value: formatPriceValue({
      currency,
      excludeDecimals,
      intl,
      negative: row.negative,
      value: row.value,
    }),
  };
};
